/* eslint-disable */ /* pre-commit-allow: SelectedItemType deliberately intersects an object shape with `string` so a hydrated option object and a raw scalar id share one display type; #32158 */
export type SelectedItemType = {
  CompanyName?: string;
  CustomerName?: string;
  FullName?: string;
  DisplayName?: string;
  Id?: string;
  Name?: string;
} & string;
/* eslint-enable */

export interface DerivedSelection {
  selectedItem: SelectedItemType | null;
  selectedItems: Array<SelectedItemType> | null;
}

/**
 * Derive a typeahead's display selection from a form-control value.
 *
 * WI 32158: the dynamicFormResetState event fires on a genuine form clear AND
 * spuriously on some relayout passes (the signature-pad landscape rotation-back
 * and a scroll on the HSE Sign In Sheet). resetSelectedItems used to blindly
 * null the display, which blanked a typeahead whose form value was still set,
 * because writeValue only re-fires on a value CHANGE. Re-deriving the display
 * from the live control value keeps a preserved value's label and clears only a
 * genuinely empty control, so a real reset still shows the placeholder.
 *
 * Pure and display-only: it reads the value the control already holds and never
 * writes it back, so it cannot change any submitted value.
 *
 * WI 32158 (review Finding 1): the HSE Sign In Sheet writes the full object into
 * the control from the modal, then the parent re-sets the control to a SCALAR id
 * (onInstructorChange -> setValue(args.Id)) while the component still holds the
 * hydrated object in selectedItem. Re-deriving from the scalar alone would render
 * the raw id instead of the name, worse than the old placeholder. So when the
 * caller passes the current hydrated selection and it still matches the live
 * control value, preserve it and derive only when there is nothing to preserve.
 */
function isObjectSelection(item: unknown): item is SelectedItemType {
  return item != null && typeof item === 'object';
}

function selectionMatchesControl(
  selectedItem: SelectedItemType,
  controlValue: unknown,
  valueProperty: string
): boolean {
  // Control still holds an object (or the same reference): treat as a match.
  if (isObjectSelection(controlValue)) {
    return true;
  }
  // Control holds a scalar id: match it against the hydrated object's id.
  const target = `${controlValue}`.toLowerCase();
  const candidates = [
    selectedItem[valueProperty as keyof SelectedItemType],
    selectedItem.Id,
    (selectedItem as { id?: string }).id,
  ];
  return candidates.some((c) => c != null && `${c}`.toLowerCase() === target);
}

export function deriveSelectedItems(
  controlValue: unknown,
  currentSelectedItem?: SelectedItemType | null,
  valueProperty = 'Id'
): DerivedSelection {
  const isEmpty =
    controlValue == null ||
    controlValue === '' ||
    (Array.isArray(controlValue) && controlValue.length === 0);
  if (isEmpty) {
    return { selectedItem: null, selectedItems: null };
  }
  // Preserve a hydrated single-select object whose id still matches the control,
  // so a spurious reset does not replace a good name pill with the raw scalar id.
  if (
    !Array.isArray(controlValue) &&
    isObjectSelection(currentSelectedItem) &&
    selectionMatchesControl(currentSelectedItem, controlValue, valueProperty)
  ) {
    return {
      selectedItem: currentSelectedItem,
      selectedItems: [currentSelectedItem],
    };
  }
  if (Array.isArray(controlValue)) {
    return {
      selectedItem: (controlValue[0] ?? null) as SelectedItemType | null,
      selectedItems: controlValue as Array<SelectedItemType>,
    };
  }
  return {
    selectedItem: controlValue as SelectedItemType,
    selectedItems: [controlValue as SelectedItemType],
  };
}
